import {
  Form,
  Gap,
  Grid,
  Heading,
  Input,
  ISelectMultiOption,
  SelectMulti,
  SingleColumnLayout,
  Span,
  Textarea,
  Alert,
} from '@salecycle/ui-component-library';
import { useUserContext } from 'core/users/UserContext';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNewRoleDispatch, useNewRoleState } from '../../context/useNewRoleContext';
import { NewRoleSidebar } from '../NewRoleSidebar';
import { updateValidStepsAction } from '../../actions/updateValidStepsAction';
import { Permission } from 'entities/user';
import { isRoleNameValid } from 'core/app/impl/isRoleNameValid';
import { isRoleDescriptionValid } from 'core/app/impl/isRoleDescriptionValid';

const hasAtLeastOnePermission = (permissions: { [key in Permission]: boolean }): boolean =>
  Object.values(permissions).some(val => !!val);

export const NewRoleSetup = () => {
  const { t } = useTranslation();
  const dispatch = useNewRoleDispatch();
  const { name, description, users, permissions, validSteps } = useNewRoleState();
  const { users: allUsers } = useUserContext();
  const usersOptions: ISelectMultiOption<string>[] = allUsers.map(user => ({
    value: user.sub,
    label: user.name,
  }));
  const [nameValid, setNameValid] = useState(isRoleNameValid(name));
  const [nameTouched, setNameTouched] = useState(name.length > 0);
  const [descriptionValid, setDescriptionValid] = useState(isRoleDescriptionValid(description));
  const [descriptionTouched, setDescriptionTouched] = useState(description.length > 0);
  const [permissionsValid, setPermissionsValid] = useState(hasAtLeastOnePermission(permissions));

  const onNameChange = (name: string) => {
    setNameValid(isRoleNameValid(name));

    if (!nameTouched) {
      setNameTouched(true);
    }

    dispatch({ type: 'UPDATE_ROLE_NAME', payload: { name } });
  };

  const onDescriptionChange = (description: string) => {
    setDescriptionValid(isRoleDescriptionValid(description));

    if (!descriptionTouched) {
      setDescriptionTouched(true);
    }

    dispatch({ type: 'UPDATE_ROLE_DESCRIPTION', payload: { description } });
  };

  const onUsersChange = (users: ISelectMultiOption<string>[]) => dispatch({ type: 'UPDATE_USERS', payload: { users } });

  useEffect(() => {
    if (!permissionsValid && hasAtLeastOnePermission(permissions)) {
      setPermissionsValid(true);
    } else if (permissionsValid && !hasAtLeastOnePermission(permissions)) {
      setPermissionsValid(false);
    }
  }, [permissions, permissionsValid]);

  useEffect(() => {
    updateValidStepsAction(1, nameValid && descriptionValid && permissionsValid, validSteps, dispatch);
  }, [nameValid, descriptionValid, permissionsValid, validSteps, dispatch]);

  return (
    <SingleColumnLayout
      paddingLeft={20}
      heading={t('newRole.roleSetup.heading')}
      showSidebar={true}
      sidebar={<NewRoleSidebar />}
      sidebarHeading={t('newRole.roleSetup.sidebar.heading')}
    >
      <Grid>
        <Form>
          <Input
            label={t('newRole.roleSetup.components.name.label')}
            ariaLabel={t('newRole.roleSetup.components.name.ariaLabel')}
            placeholder={t('newRole.roleSetup.components.name.placeholder')}
            onChange={onNameChange}
            validationState={nameValid && nameTouched ? 'complete' : nameTouched ? 'warning' : null}
            validationMessage={
              !nameValid && nameTouched ? t('newRole.roleSetup.components.name.validationMessage') : null
            }
            value={name}
          />
          <Textarea
            label={t('newRole.roleSetup.components.description.label')}
            ariaLabel={t('newRole.roleSetup.components.description.ariaLabel')}
            placeholder={t('newRole.roleSetup.components.description.placeholder')}
            onChange={onDescriptionChange}
            validationState={
              descriptionValid && descriptionTouched ? 'complete' : descriptionTouched ? 'warning' : null
            }
            validationMessage={
              !descriptionValid && descriptionTouched
                ? t('newRole.roleSetup.components.description.validationMessage')
                : null
            }
            value={description}
          />
          {!hasAtLeastOnePermission(permissions) && (
            <>
              <Gap top={20} />
              <Alert variation="warning" heading={t('newRole.roleSetup.components.permissions.validationHeading')}>
                {t('newRole.roleSetup.components.permissions.validationMessage')}
              </Alert>
              <Gap top={20} />
            </>
          )}
          {hasAtLeastOnePermission(permissions) && <Gap top={40} />}
          <Heading variation="h2">{t('newRole.roleSetup.usersSection.heading')}</Heading>
          <Span>{t('newRole.roleSetup.usersSection.description')}</Span>
          <Gap top={30} />
          <SelectMulti
            label={t('newRole.roleSetup.usersSection.components.userInput.label')}
            ariaLabel={t('newRole.roleSetup.usersSection.components.userInput.ariaLabel')}
            options={usersOptions}
            clearable={true}
            onChange={onUsersChange}
            value={users}
          />
        </Form>
      </Grid>
    </SingleColumnLayout>
  );
};
